/**
 * Brand mode for the core results page (Facets.Browse.View).
 *
 * /<slug>[/<category path>][?options] is registered on the core "facet-browse"
 * page type — the same registration the core's Facets.Router.addUrl() performs
 * for category URLs — so the page renders through the normal view, template,
 * sorting, paging and facets. When the URL belongs to a brand, the view's
 * translator is rebuilt so that:
 *   - the manufacturer facet is applied (custitem_web_manufacturer=<value>)
 *   - an optional category path scopes it (commercecategoryurl=/blast/equipment)
 *   - every URL it generates stays under /<slug> and omits the implied facet
 *
 * Safe Mode: Facets.Browse.View is on the core's "tolerated" list (loads, logs
 * a console warning — as NetSuite's own bundled extensions do). Nothing private
 * is required: the Translator class is taken from the view's own instance.
 *
 * Translator facts this relies on (SCS 2026.1): constructor(url, options,
 * configuration, categoryUrl) — a STRING 4th argument sets categoryUrl, which
 * makes getApiParams() add commercecategoryurl; getUrl() starts from
 * categoryUrl or "/" + configuration.fallbackUrl; cloneForFacetId() TOGGLES a
 * multi facet, so it is only called when the facet is absent.
 */
define('Clemtex.Brands.Browse', [
    'Facets.Browse.View',
    'Clemtex.Brands.Utils',
    'Clemtex.Brands.Tiles',
    'Clemtex.Brands.FacetUrls',
    'Backbone',
    'jQuery',
    'underscore'
], function (FacetsBrowseView, Utils, Tiles, FacetUrls, Backbone, jQuery, _) {
    'use strict';

    var FACET_ID = Utils.FACET_ID;
    var PAGE_CLASS = 'clemtex-brands-page';
    var installed = false;
    var translatorPatched = false;
    var aliasMap = null;

    // One getUrl patch for both features, in a fixed order: the brand rewrite
    // needs the raw facet ids (it drops custitem_web_manufacturer=<value>), and
    // the facet-name aliases are applied last, to whatever URL results.
    function patchTranslator(Translator) {
        if (translatorPatched || !Translator || !Translator.prototype || !Translator.prototype.getUrl) { return; }
        translatorPatched = true;
        var originalGetUrl = Translator.prototype.getUrl;
        Translator.prototype.getUrl = function getUrl() {
            var url = originalGetUrl.apply(this, arguments);
            var brand = this.configuration && this.configuration.clemtexBrand;
            if (brand) { url = Utils.rewriteUrl(url, brand, FACET_ID); }
            return FacetUrls.aliasUrl(url, aliasMap);
        };
    }

    // Hide the brand's own filter chip; hide the whole "Filtered By" row when
    // it is the only chip (CSS keys off these classes).
    function tidyChips(view) {
        var brand = view.clemtexBrand;
        var $display = view.$('[data-view="Facets.FacetsDisplay"]');
        var $chips = $display.find('.facets-facets-display-filter');
        var $brandChip = $chips.filter(function () {
            return jQuery(this).attr('data-facet-id') === brand.facetValue;
        }).addClass('clemtex-brands-chip');
        $display.toggleClass('clemtex-brands-only', $chips.length - $brandChip.length === 0);
    }

    // Stand-in for the core's Categories.Model (a private module under Safe
    // Mode). Everything category-specific on a results page keys off
    // view.model.get('category'): the category heading (banner image + title),
    // the PLP component's getCategoryInfo() — which the Multi Add To Cart
    // extension uses to recognise diagram categories, switch to its numbered
    // display and reveal each item's position — and the theme's sibling menu.
    // Same endpoint and shape as the core model; deepCopy() must return the
    // plain attributes because Utils.deepCopy(model) delegates to it.
    var CategoryModel = Backbone.Model.extend({
        idAttribute: 'internalid',
        initialize: function (attrs, options) {
            this.clemtexBrand = options && options.brand;
            this.serviceUrl = options && options.url;
        },
        url: function () { return this.serviceUrl; },
        deepCopy: function () { return this.attributes; },
        parse: function (response) {
            var data = response && response.data && response.data[0];
            if (!data) { return response; }
            var label = this.clemtexBrand && this.clemtexBrand.label;
            var heading = data.pageheading || data.name || '';
            if (label && heading && heading.toLowerCase().indexOf(label.toLowerCase()) === -1) {
                data.pageheading = label + ' ' + heading;     // "Clemco Blast Equipment & Supplies"
            }
            return data;
        }
    });

    // The theme fills #js-div-navs with the category's sibling links (raw
    // category URLs); keep them inside the brand.
    function scopeSiblingLinks(view) {
        var brand = view.clemtexBrand;
        view.$('#js-div-navs a[href^="/"]').each(function () {
            var $a = jQuery(this);
            var href = $a.attr('href');
            if (href.indexOf('/' + brand.slug + '/') !== 0) { $a.attr('href', Utils.brandUrl(brand, href)); }
        });
    }

    // On a prerendered page the core "rehydrates": the first view is handed the
    // server-rendered root element and its child views bind to the existing
    // markup instead of being appended (SC.isRehydrating). When NetSuite's
    // prerenderer served a 404 for a brand URL (its caches lag a new brand by
    // hours), that root is the 404 page, so the item grid renders into a
    // detached element and never shows. Detect the mismatch and fall back to a
    // normal fresh render with a root of our own.
    function leaveRehydrationIfMismatched(view) {
        if (!window.SC || !SC.isRehydrating) { return; }
        var attrs = _.result(view, 'attributes') || {};
        var rootClass = String(attrs['class'] || '').split(/\s+/).filter(Boolean);
        var looksRight = _.every(rootClass, function (cls) { return view.$el.hasClass(cls); });
        if (looksRight) { return; }
        SC.isRehydrating = false;
        var $fresh = jQuery('<' + (_.result(view, 'tagName') || 'div') + '/>').attr(attrs);
        view.setElement($fresh);
    }

    return {
        install: function install(brandMap, settings, pageTypeComponent) {
            var hasBrands = !_.isEmpty(brandMap);
            aliasMap = settings.facetUrls && !settings.facetUrls.empty ? settings.facetUrls : null;
            if (installed || (!hasBrands && !aliasMap)) { return; }
            installed = true;
            if (hasBrands) { Tiles.install(); }
            FacetUrls.installHistory(Backbone, aliasMap);

            var proto = FacetsBrowseView.prototype;

            var originalInitialize = proto.initialize;
            proto.initialize = function initialize() {
                originalInitialize.apply(this, arguments);
                // Every results page (category, search, brand): patch before the
                // first render so all facet links come out aliased.
                if (this.translator) { patchTranslator(this.translator.constructor); }
                var context = hasBrands && Utils.parseFragment(Backbone.history.fragment, brandMap);
                if (!context) { return; }
                try {
                    var brand = context.brand;
                    var config = _.extend({}, this.translatorConfig, { fallbackUrl: brand.slug, clemtexBrand: brand });
                    var Translator = this.translator.constructor;
                    patchTranslator(Translator);
                    var translator = new Translator('?' + context.query, null, config, context.categoryUrl || false);
                    if (!_.some(translator.getAllFacets(), function (facet) { return facet.id === FACET_ID; })) {
                        translator = translator.cloneForFacetId(FACET_ID, brand.facetValue);
                    }
                    this.clemtexBrand = brand;
                    this.clemtexCategoryUrl = context.categoryUrl;
                    this.clemtexChain = [];
                    this.translatorConfig = config;
                    this.translator = translator;
                    var navUrl = context.categoryUrl && Tiles.categoryNavUrl(context.categoryUrl);
                    this.isCategoryPage = !!navUrl;
                    if (navUrl) {
                        // fetched by the core alongside the items (beforeShowContent)
                        this.model.set('category', new CategoryModel({}, { brand: brand, url: navUrl }));
                    } else {
                        // The core decides "category page" by URL prefix, so a slug that
                        // starts with a top-level category (blast-it-all vs /blast) gets a
                        // category model for "/blast-it-all"; its failed fetch 404s the page.
                        this.model.unset('category');
                    }
                    this.setOptionsTranslator();
                    this.model.options.data = this.translator.getApiParams();
                    leaveRehydrationIfMismatched(this);
                    this.$el.addClass(PAGE_CLASS);

                    var self = this;
                    this.on('afterViewRender', function () {
                        tidyChips(self);
                        setTimeout(function () { tidyChips(self); scopeSiblingLinks(self); }, 0);
                        setTimeout(function () { scopeSiblingLinks(self); }, 400);   // after the theme's menu script
                        Tiles.render(self, settings);
                    });
                } catch (e) {
                    if (window.console) { window.console.error('ClemtexBrands brand mode failed', e); }
                }
            };

            // Resolve the category chain before the page shows so the breadcrumb
            // and title are right on first render.
            var originalBeforeShowContent = proto.beforeShowContent;
            proto.beforeShowContent = function beforeShowContent() {
                var self = this;
                var result = originalBeforeShowContent.apply(this, arguments);
                if (!this.clemtexBrand || !this.clemtexCategoryUrl) { return result; }
                var done = jQuery.Deferred();
                jQuery.when(result).then(function () {
                    Tiles.getTree().then(function (tree) {
                        self.clemtexChain = Utils.chainTo(tree, self.clemtexCategoryUrl) || [];
                    }).always(function () { done.resolve(); });
                }, function () { done.reject.apply(done, arguments); });
                return done.promise();
            };

            var originalBreadcrumb = proto.getBreadcrumbPages;
            proto.getBreadcrumbPages = function getBreadcrumbPages() {
                if (!this.clemtexBrand) { return originalBreadcrumb.apply(this, arguments); }
                var brand = this.clemtexBrand;
                return [{ text: brand.label, href: Utils.brandUrl(brand) }].concat(_.map(this.clemtexChain, function (node) {
                    return { text: node.name, href: Utils.brandUrl(brand, node.fullurl) };
                }));
            };

            var originalGetTitle = proto.getTitle;
            proto.getTitle = function getTitle() {
                if (!this.clemtexBrand) { return originalGetTitle.apply(this, arguments); }
                return Utils.pageTitle(this.clemtexBrand, this.clemtexChain, settings.siteName);
            };

            var originalMetaDescription = proto.getMetaDescription;
            proto.getMetaDescription = function getMetaDescription() {
                if (this.clemtexBrand && !this.clemtexCategoryUrl && this.clemtexBrand.metaDescription) {
                    return this.clemtexBrand.metaDescription;
                }
                return originalMetaDescription.apply(this, arguments);
            };

            var originalCanonical = proto.getCanonical;
            proto.getCanonical = function getCanonical() {
                if (!this.clemtexBrand) { return originalCanonical.apply(this, arguments); }
                return window.location.protocol + '//' + window.location.hostname +
                    Utils.brandUrl(this.clemtexBrand, this.clemtexCategoryUrl);
            };

            if (hasBrands) {
                pageTypeComponent.registerPageType({
                    name: 'facet-browse',
                    routes: Utils.routePatterns(_.keys(brandMap)),
                    view: FacetsBrowseView
                });
            }
        }
    };
});
